import React, { useState } from 'react';
import { Modal, Button, Badge, Card, Row, Col } from 'react-bootstrap';

const CompleteAuditModal = ({ show, onHide, onConfirm, isLoading, reportData }) => {
  const [expandedSections, setExpandedSections] = useState({});
  
  if (!reportData) return null;

  const { checklist, auditor, supplier, branch, responses, summary, sectionScores, sections } = reportData;
  const BASE_URL = process.env.REACT_APP_API_URL;

  const getComplianceColor = (percentage) => {
    if (percentage >= 90) return 'success';
    if (percentage >= 70) return 'warning';
    return 'danger';
  };

  const getPointsColor = (points) => {
    if (points >= 8) return 'success';
    if (points >= 5) return 'warning';
    return 'danger';
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const expandAll = () => {
    const allSections = {};
    sections.forEach((section, idx) => {
      allSections[`section-${idx}`] = true;
      if (section.subsections) {
        section.subsections.forEach((subsection, subIdx) => {
          allSections[`subsection-${idx}-${subIdx}`] = true;
          if (subsection.subsections) {
            subsection.subsections.forEach((subsubsection, subsubIdx) => {
              allSections[`subsubsection-${idx}-${subIdx}-${subsubIdx}`] = true;
            });
          }
        });
      }
    });
    setExpandedSections(allSections);
  };

  const collapseAll = () => {
    setExpandedSections({});
  };

  const renderMediaFiles = (mediaFile) => {
    if (!mediaFile || mediaFile.trim().length === 0) return null;

    let mediaFiles = [];
    try {
      mediaFiles = JSON.parse(mediaFile);
      if (!Array.isArray(mediaFiles)) {
        mediaFiles = [mediaFile];
      }
      mediaFiles = mediaFiles.filter(file => file && file.trim().length > 0);
    } catch (err) {
      if (mediaFile && mediaFile.trim().length > 0) {
        mediaFiles = [mediaFile];
      }
    }

    if (mediaFiles.length === 0) return null;

    return (
      <div className="mt-2">
        <small className="text-muted d-block mb-1">Evidence:</small>
        <div className="d-flex flex-wrap gap-1">
          {mediaFiles.map((file, idx) => {
            const imageUrl = file.startsWith('http') ? file : `${BASE_URL}/uploads/audit-media/${file}`;
            return (
              <div key={idx} className="evidence-thumbnail">
                <img
                  src={imageUrl}
                  alt={`Evidence ${idx + 1}`}
                  className="img-thumbnail"
                  style={{ width: '50px', height: '40px', objectFit: 'cover' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div 
                  className="d-none align-items-center justify-content-center bg-light border rounded text-muted"
                  style={{ width: '50px', height: '40px', fontSize: '10px' }}
                >
                  No Image
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderQuestion = (questionId, questionIndex, sectionPath = '') => {
    const response = responses.find(r => r.question_id === questionId);
    if (!response) return null;

    return (
      <Card key={questionId} className="mb-2 question-card">
        <Card.Body className="p-2">
          <div className="d-flex justify-content-between align-items-start mb-1">
            <div className="flex-grow-1">
              <div className="d-flex align-items-center mb-1">
                <Badge bg="secondary" className="me-2" style={{ fontSize: '0.7rem' }}>
                  Q{questionIndex}
                </Badge>
                <small className="text-muted" style={{ fontSize: '0.75rem' }}>{sectionPath}</small>
              </div>
              <h6 className="mb-1" style={{ fontSize: '0.9rem' }}>{response.questionText || 'No question text'}</h6>
            </div>
            <div className="text-end ms-2">
              <Badge 
                bg={getPointsColor(response.points)} 
                className="mb-1 d-block"
                style={{ fontSize: '0.7rem' }}
              >
                {response.points}/10
              </Badge>
              {response.cap === 'Yes' && (
                <Badge bg="warning" className="d-block" style={{ fontSize: '0.6rem' }}>
                  CAP
                </Badge>
              )}
            </div>
          </div>

          <Row className="g-2">
            <Col md={6}>
              <div className="p-2 bg-light rounded">
                <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>Answer:</small>
                <strong style={{ fontSize: '0.85rem' }}>{response.answer || 'No answer'}</strong>
              </div>
            </Col>
            {response.remarks && (
              <Col md={6}>
                <div className="p-2 bg-light rounded">
                  <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>Remarks:</small>
                  <div className="small" style={{ fontSize: '0.8rem' }}>{response.remarks}</div>
                </div>
              </Col>
            )}
          </Row>

          {renderMediaFiles(response.media_file)}
        </Card.Body>
      </Card>
    );
  };

  const renderSubSubSection = (subsubsection, sectionIdx, subsectionIdx, subsubsectionIdx, parentPath) => {
    const sectionKey = `subsubsection-${sectionIdx}-${subsectionIdx}-${subsubsectionIdx}`;
    const sectionPath = `${parentPath} > ${subsubsection.name}`;
    const isExpanded = expandedSections[sectionKey];

    return (
      <div key={subsubsectionIdx} className="mb-2">
        <div 
          className="d-flex justify-content-between align-items-center p-2 bg-info bg-opacity-10 rounded cursor-pointer border"
          onClick={() => toggleSection(sectionKey)}
        >
          <h6 className="mb-0 text-info" style={{ fontSize: '0.9rem' }}>
            <i className={`fas ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'} me-1`} style={{ fontSize: '0.8rem' }}></i>
            {subsubsection.name}
          </h6>
          <Badge bg="info" style={{ fontSize: '0.7rem' }}>
            {subsubsection.questions ? subsubsection.questions.length : 0}
          </Badge>
        </div>
        
        {isExpanded && subsubsection.questions && (
          <div className="mt-2 ps-3">
            {subsubsection.questions.map((questionId, qIdx) => 
              renderQuestion(questionId, qIdx + 1, sectionPath)
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSubSection = (subsection, sectionIdx, subsectionIdx, parentPath) => {
    const sectionKey = `subsection-${sectionIdx}-${subsectionIdx}`;
    const sectionPath = `${parentPath} > ${subsection.name}`;
    const isExpanded = expandedSections[sectionKey];

    return (
      <div key={subsectionIdx} className="mb-2">
        <div 
          className="d-flex justify-content-between align-items-center p-2 bg-warning bg-opacity-10 rounded cursor-pointer border"
          onClick={() => toggleSection(sectionKey)}
        >
          <h5 className="mb-0 text-warning-emphasis" style={{ fontSize: '1rem' }}>
            <i className={`fas ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'} me-1`} style={{ fontSize: '0.9rem' }}></i>
            {subsection.name}
          </h5>
          <Badge bg="warning" style={{ fontSize: '0.7rem' }}>
            {(subsection.questions ? subsection.questions.length : 0) + 
             (subsection.subsections ? subsection.subsections.reduce((acc, sub) => acc + (sub.questions ? sub.questions.length : 0), 0) : 0)}
          </Badge>
        </div>
        
        {isExpanded && (
          <div className="mt-2 ps-2">
            {/* Render direct questions in subsection */}
            {subsection.questions && subsection.questions.map((questionId, qIdx) => 
              renderQuestion(questionId, qIdx + 1, sectionPath)
            )}
            
            {/* Render sub-subsections */}
            {subsection.subsections && subsection.subsections.map((subsubsection, subsubIdx) =>
              renderSubSubSection(subsubsection, sectionIdx, subsectionIdx, subsubIdx, sectionPath)
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSection = (section, sectionIdx) => {
    const sectionKey = `section-${sectionIdx}`;
    const isExpanded = expandedSections[sectionKey];
    const sectionScore = sectionScores[section.name];
    const compliancePercentage = sectionScore ? Math.round((sectionScore.score / sectionScore.total) * 100) : 0;

    const totalQuestions = (section.questions ? section.questions.length : 0) + 
                          (section.subsections ? section.subsections.reduce((acc, sub) => 
                            acc + (sub.questions ? sub.questions.length : 0) + 
                            (sub.subsections ? sub.subsections.reduce((subacc, subsub) => 
                              subacc + (subsub.questions ? subsub.questions.length : 0), 0) : 0), 0) : 0);

    return (
      <div key={sectionIdx} className="mb-3">
        <div 
          className="d-flex justify-content-between align-items-center p-2 bg-primary bg-opacity-10 rounded cursor-pointer border"
          onClick={() => toggleSection(sectionKey)}
        >
          <h4 className="mb-0 text-primary" style={{ fontSize: '1.1rem' }}>
            <i className={`fas ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'} me-1`} style={{ fontSize: '1rem' }}></i>
            {section.name}
          </h4>
          <Badge bg="primary" style={{ fontSize: '0.8rem' }}>
            {totalQuestions} questions
          </Badge>
        </div>
        
        {isExpanded && (
          <div className="mt-2 ps-1">
            {/* Render direct questions in section */}
            {section.questions && section.questions.map((questionId, qIdx) => 
              renderQuestion(questionId, qIdx + 1, section.name)
            )}
            
            {/* Render subsections */}
            {section.subsections && section.subsections.map((subsection, subIdx) =>
              renderSubSection(subsection, sectionIdx, subIdx, section.name)
            )}
          </div>
        )}
      </div>
    );
  };

  const overallCompliance = summary.total > 0 ? Math.round((summary.score / summary.total) * 100) : 0;

  return (
    <Modal show={show} onHide={onHide} size="lg" className="complete-audit-modal">
      <Modal.Header closeButton className="bg-primary text-white py-2">
        <Modal.Title style={{ fontSize: '1.1rem' }}>
          <i className="fas fa-check-circle me-2"></i>
          Complete Audit Confirmation
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="p-0">
        {/* Controls */}
        <div className="p-2 bg-light border-bottom">
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-primary" onClick={expandAll}>
              <i className="fas fa-expand-alt me-1"></i>
              Expand All
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={collapseAll}>
              <i className="fas fa-compress-alt me-1"></i>
              Collapse All
            </Button>
          </div>
        </div>

        {/* Sections Content */}
        <div className="audit-content p-2" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {sections.map((section, idx) => renderSection(section, idx))}
        </div>
      </Modal.Body>
      
      <Modal.Footer className="bg-light py-2">
        <Button variant="secondary" size="sm" onClick={onHide}>
          <i className="fas fa-times me-1"></i>
          Cancel
        </Button>
        <Button variant="success" size="sm" onClick={onConfirm} disabled={isLoading}>
          {isLoading ? (
            <>
              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
              Completing...
            </>
          ) : (
            <>
              <i className="fas fa-check-circle me-1"></i>
              Complete Audit
            </>
          )}
        </Button>
      </Modal.Footer>

      <style jsx>{`
        .complete-audit-modal .modal-dialog {
          max-width: 900px;
        }
        
        .cursor-pointer {
          cursor: pointer;
        }
        
        .cursor-pointer:hover {
          background-color: rgba(0, 0, 0, 0.05) !important;
        }
        
        .question-card {
          border-left: 3px solid #0d6efd;
          transition: all 0.2s;
        }
        
        .question-card:hover {
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        .evidence-thumbnail {
          position: relative;
        }
        
        .evidence-thumbnail:hover img {
          transform: scale(1.05);
          transition: transform 0.2s;
        }
        
        .audit-content {
          scrollbar-width: thin;
          scrollbar-color: #999 #f1f1f1;
        }
        
        .audit-content::-webkit-scrollbar {
          width: 6px;
        }
        
        .audit-content::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }
        
        .audit-content::-webkit-scrollbar-thumb {
          background: #999;
          border-radius: 3px;
        }
        
        .audit-content::-webkit-scrollbar-thumb:hover {
          background: #777;
        }
      `}</style>
    </Modal>
  );
};

export default CompleteAuditModal;